/**
 * P1-G6 — sound and haptics.
 *
 * Pure module (no React, no Redux): synthesised Web Audio tones, so no audio
 * files ship, plus `navigator.vibrate`. Every entry point is safe to call
 * anywhere: it never throws and silently does nothing when the platform lacks
 * the API, the user turned the feature off, or the browser blocks audio.
 *
 *  - Sound follows the `sound_enabled` game setting; callers pass it in
 *    (`useFeedback` reads it from the store).
 *  - Haptics follow the `hc_haptics` localStorage flag (default on) until the
 *    backend grows a `haptics_enabled` setting, and are suppressed under
 *    `prefers-reduced-motion`.
 *  - The `AudioContext` is created lazily from a user gesture (autoplay
 *    policy): `armAudioUnlock` installs one-shot gesture listeners, and
 *    `playTone` never creates a context itself outside of one.
 */

export type ToneKind = 'done' | 'skip' | 'timer' | 'handoff' | 'reveal';

export type VibrationPattern = number | number[];

export const HAPTICS_STORAGE_KEY = 'hc_haptics';
export const HAPTICS_CHANGE_EVENT = 'hc:haptics-change';

/** Named vibration patterns (ms). Done is two short pulses, Skip one. */
export const VIBRATION_PATTERNS = {
  done: [30, 50, 30],
  skip: 30,
  timer: 200,
  handoff: [20, 40, 20],
  reveal: [25, 40, 60],
} as const satisfies Record<ToneKind, VibrationPattern>;

interface ToneStep {
  /** Frequency in Hz. */
  frequency: number;
  /** Offset from the start of the tone, in seconds. */
  at: number;
  /** Length of the note, in seconds. */
  duration: number;
  /** Peak gain, 0..1. Kept low: these are soft UI cues. */
  gain: number;
}

export const TONES: Record<ToneKind, ToneStep[]> = {
  done: [
    { frequency: 660, at: 0, duration: 0.09, gain: 0.07 },
    { frequency: 880, at: 0.09, duration: 0.14, gain: 0.07 },
  ],
  skip: [{ frequency: 330, at: 0, duration: 0.14, gain: 0.06 }],
  timer: [
    { frequency: 880, at: 0, duration: 0.45, gain: 0.06 },
    { frequency: 1320, at: 0, duration: 0.45, gain: 0.03 },
  ],
  handoff: [
    { frequency: 523, at: 0, duration: 0.12, gain: 0.06 },
    { frequency: 659, at: 0.12, duration: 0.18, gain: 0.06 },
  ],
  reveal: [
    { frequency: 523, at: 0, duration: 0.1, gain: 0.06 },
    { frequency: 659, at: 0.1, duration: 0.1, gain: 0.06 },
    { frequency: 784, at: 0.2, duration: 0.22, gain: 0.06 },
  ],
};

// ─── Haptics preference ───────────────────────────────────────────────────────

export function getHapticsEnabled(): boolean {
  try {
    return window.localStorage.getItem(HAPTICS_STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setHapticsEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(HAPTICS_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Storage blocked: the toggle still applies for this page via the event.
  }
  try {
    window.dispatchEvent(new Event(HAPTICS_CHANGE_EVENT));
  } catch {
    // No window (SSR): nothing to notify.
  }
}

export function prefersReducedMotion(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch {
    return false;
  }
}

// ─── Vibration ────────────────────────────────────────────────────────────────

export interface VibrateOptions {
  /** Overrides the stored haptics preference (used by the store-bound hook). */
  enabled?: boolean;
}

/** Vibrates when supported, enabled and not under reduced motion. */
export function vibrate(
  pattern: VibrationPattern,
  { enabled }: VibrateOptions = {}
): boolean {
  try {
    if (!(enabled ?? getHapticsEnabled())) return false;
    if (prefersReducedMotion()) return false;
    if (typeof navigator === 'undefined') return false;
    if (typeof navigator.vibrate !== 'function') return false;
    return navigator.vibrate(pattern);
  } catch {
    return false;
  }
}

// ─── Audio ────────────────────────────────────────────────────────────────────

type AudioContextCtor = new () => AudioContext;

let audioContext: AudioContext | null = null;
let unlockArmed = false;

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** Creates (once) and resumes the shared context. Call from a user gesture. */
function unlockAudioContext(): AudioContext | null {
  try {
    if (!audioContext) {
      const Ctor = getAudioContextCtor();
      if (!Ctor) return null;
      audioContext = new Ctor();
    }
    if (audioContext.state === 'suspended') {
      void Promise.resolve(audioContext.resume()).catch(() => undefined);
    }
    return audioContext;
  } catch {
    return null;
  }
}

const UNLOCK_EVENTS = [
  'pointerdown',
  'touchstart',
  'keydown',
  'click',
] as const;

/**
 * Installs one-shot gesture listeners that create the `AudioContext` on the
 * first tap or key press. Idempotent; returns a disposer.
 */
export function armAudioUnlock(): () => void {
  if (unlockArmed || typeof document === 'undefined') {
    return () => undefined;
  }
  unlockArmed = true;

  const dispose = () => {
    for (const type of UNLOCK_EVENTS) {
      document.removeEventListener(type, onGesture, true);
    }
    unlockArmed = false;
  };
  function onGesture() {
    unlockAudioContext();
    dispose();
  }
  for (const type of UNLOCK_EVENTS) {
    document.addEventListener(type, onGesture, true);
  }
  return dispose;
}

export interface PlayToneOptions {
  /** The `sound_enabled` setting. Defaults to on when omitted. */
  enabled?: boolean;
}

/**
 * Plays a short synthesised cue. Returns true when notes were scheduled.
 * Does nothing (and never throws) when sound is off, the platform has no Web
 * Audio, or no user gesture has unlocked the context yet.
 */
export function playTone(
  kind: ToneKind,
  { enabled = true }: PlayToneOptions = {}
): boolean {
  try {
    if (!enabled) return false;
    const ctx = audioContext;
    if (!ctx) return false;
    if (ctx.state === 'suspended') {
      void Promise.resolve(ctx.resume()).catch(() => undefined);
    }

    const start = ctx.currentTime + 0.01;
    for (const step of TONES[kind]) {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      const begin = start + step.at;
      const end = begin + step.duration;

      oscillator.type = 'sine';
      oscillator.frequency.value = step.frequency;
      gain.gain.setValueAtTime(0.0001, begin);
      gain.gain.linearRampToValueAtTime(step.gain, begin + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);

      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(begin);
      oscillator.stop(end + 0.02);
    }
    return true;
  } catch {
    return false;
  }
}

/** Test helper: forgets the shared context and the armed listeners. */
export function resetFeedbackForTests(): void {
  audioContext = null;
  unlockArmed = false;
}

/** Fires the matching cue and vibration for a game moment. */
export function playCue(
  kind: ToneKind,
  options: { soundEnabled?: boolean; hapticsEnabled?: boolean } = {}
): void {
  playTone(kind, { enabled: options.soundEnabled });
  vibrate(VIBRATION_PATTERNS[kind], { enabled: options.hapticsEnabled });
}

/** Direct access for gesture handlers that want the context ready right now. */
export function primeAudio(): void {
  unlockAudioContext();
}
